/*!
=========================================================
* Muse Ant Design Dashboard - v1.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { Switch, Route, Redirect } from "react-router-dom";
import { useEffect } from "react";
import Home from "./pages/Home";
import Tables from "./pages/Tables";
import Billing from "./pages/Billing";
import Providers from "./pages/Providers";
import Assistants from "./pages/Assistants";
import Flows from "./pages/Flows";
import FlowEditor from "./pages/FlowEditor";
import Scripts from "./pages/Scripts";
import ScriptDetail from "./pages/ScriptDetail";
import GoogleWorkspace from "./pages/GoogleWorkspace";
import AutoChannels from "./pages/AutoChannels";
import AutoConfigs from "./pages/AutoConfigs";
import AutoConfigDetail from "./pages/AutoConfigDetail";
import AutoUploadTargets from "./pages/AutoUploadTargets";
import AutoTiktokAccounts from "./pages/AutoTiktokAccounts";
import AutoFacebookPages from "./pages/AutoFacebookPages";
import AutoWorkerAgents from "./pages/AutoWorkerAgents";
import SystemUsers from "./pages/SystemUsers";
import SystemFeedbacks from "./pages/SystemFeedbacks";
import SystemYoutubeCredentials from "./pages/SystemYoutubeCredentials";
import SystemAppParams from "./pages/SystemAppParams";
import VideoFfmpegCommands from "./pages/VideoFfmpegCommands";
import AiVideoProjects, { IllustratedVideoProjects } from "./pages/AiVideoProjects";
import AiVideoProjectDetail from "./pages/AiVideoProjectDetail";
import Logout from "./pages/Logout";
import { isAuthenticated } from "./services/authService";
import { canAccessAiProviders, canUseAiVideoProjects } from "./services/sessionProfile";
import { SessionProfileProvider, useSessionProfile } from "./services/SessionProfileContext";
import Rtl from "./pages/Rtl";
import Profile from "./pages/Profile";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import Main from "./components/layout/Main";
import FeedbackWidget from "./components/feedback/FeedbackWidget";
import "antd/dist/antd.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import "./assets/styles/compact.css";

function PrivateRoute({ component: Component, ...routeProps }) {
  return (
    <Route
      {...routeProps}
      render={(props) =>
        isAuthenticated() ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/sign-in",
              state: { from: `${props.location.pathname}${props.location.search}` },
            }}
          />
        )
      }
    />
  );
}

function ProviderAccessDenied() {
  return <div className="page-heading"><div><h3>Bạn không có quyền quản lý nhà cung cấp AI.</h3><p>Gói AI Video hiện tại không cho phép truy cập trang này.</p></div></div>;
}

export function ProviderRoute({ component: Component, ...routeProps }) {
  const { profile, status } = useSessionProfile();
  return (
    <Route
      {...routeProps}
      render={(props) => {
        if (!isAuthenticated()) {
          return <Redirect to={{ pathname: "/sign-in", state: { from: `${props.location.pathname}${props.location.search}` } }} />;
        }
        if (status === "loading") return <div className="page-heading">Đang kiểm tra quyền truy cập...</div>;
        return status === "ready" && canAccessAiProviders(profile) ? <Component {...props} /> : <ProviderAccessDenied />;
      }}
    />
  );
}

function AiVideoAccessDenied() {
  return <div className="page-heading"><div><h3>AI Video chưa khả dụng cho tài khoản này.</h3><p>Gói hiện tại không cho phép truy cập không gian dự án AI Video.</p></div></div>;
}

export function AiVideoRoute({ component: Component, ...routeProps }) {
  const { profile, status } = useSessionProfile();
  return <Route {...routeProps} render={(props) => {
    if (!isAuthenticated()) {
      return <Redirect to={{ pathname: "/sign-in", state: { from: `${props.location.pathname}${props.location.search}` } }} />;
    }
    if (status === "loading") return <div className="page-heading">Đang kiểm tra quyền truy cập AI Video...</div>;
    return status === "ready" && canUseAiVideoProjects(profile) ? <Component {...props} /> : <AiVideoAccessDenied />;
  }} />;
}

function SessionWatcher() {
  useEffect(() => {
    const checkSession = () => {
      const publicAuthPath = window.location.pathname.startsWith("/sign-in") || window.location.pathname.startsWith("/sign-up");
      if (!publicAuthPath && !isAuthenticated()) window.location.assign("/sign-in");
    };
    const timer = window.setInterval(checkSession, 30000);
    checkSession();
    return () => window.clearInterval(timer);
  }, []);

  return null;
}

function App() {
  return (
    <SessionProfileProvider><div className="App">
      <SessionWatcher />
      <Switch>
        <Route path="/sign-up" exact component={SignUp} />
        <Route path="/sign-in" exact component={SignIn} />
        <Route path="/logout" exact component={Logout} />
        <Main>
          <Switch>
            <PrivateRoute exact path="/dashboard" component={Home} />
            <ProviderRoute exact path="/providers" component={Providers} />
            <PrivateRoute exact path="/assistants" component={Assistants} />
            <PrivateRoute exact path="/flows" component={Flows} />
            <PrivateRoute exact path="/flows/:id" component={FlowEditor} />
            <PrivateRoute exact path="/scripts" component={Scripts} />
            <PrivateRoute exact path="/scripts/:id" component={ScriptDetail} />
            <PrivateRoute exact path="/google-workspace" component={GoogleWorkspace} />
            <PrivateRoute exact path="/video-tools/ffmpeg" component={VideoFfmpegCommands} />
            <AiVideoRoute exact path="/video-tools/ai-video" component={AiVideoProjects} />
            <AiVideoRoute exact path="/video-tools/ai-video/:id" component={AiVideoProjectDetail} />
            <AiVideoRoute exact path="/video-tools/illustrated-video" component={IllustratedVideoProjects} />
            <AiVideoRoute exact path="/video-tools/illustrated-video/:id" component={AiVideoProjectDetail} />
            <PrivateRoute exact path="/auto-upload/groups" component={AutoUploadTargets} />
            <PrivateRoute exact path="/auto-upload/channels" component={AutoChannels} />
            <PrivateRoute exact path="/auto-upload/tiktok-accounts" component={AutoTiktokAccounts} />
            <PrivateRoute exact path="/auto-upload/facebook-pages" component={AutoFacebookPages} />
            <PrivateRoute exact path="/auto-upload/configs" component={AutoConfigs} />
            <PrivateRoute exact path="/auto-upload/configs/:id" component={AutoConfigDetail} />
            <PrivateRoute exact path="/system/users" component={SystemUsers} />
            <PrivateRoute exact path="/system/app-params" component={SystemAppParams} />
            <PrivateRoute exact path="/system/workers" component={AutoWorkerAgents} />
            <PrivateRoute exact path="/system/feedbacks" component={SystemFeedbacks} />
            <PrivateRoute exact path="/system/youtube-credentials" component={SystemYoutubeCredentials} />
            <Redirect exact from="/auto-upload/workers" to="/system/workers" />
            <Route exact path="/tables" component={Tables} />
            <Route exact path="/billing" component={Billing} />
            <Route exact path="/rtl" component={Rtl} />
            <PrivateRoute exact path="/profile" component={Profile} />
            <Redirect from="*" to="/dashboard" />
          </Switch>
          {isAuthenticated() && <FeedbackWidget />}
        </Main>
      </Switch>
    </div></SessionProfileProvider>
  );
}

export default App;
